/**
 * PRIVACY: Geen user-input naar server, localStorage (behalve onboarding-flag
 * en preview-teller), externe APIs. PortalAuth verify via SDK; minimale Umami
 * analytics (tool_open/close).
 */
import { useCallback, useEffect, useState } from "react";
import {
  ToolLayout,
  ToolTabs,
  Onboarding,
  useAccess,
  hasCompletedOnboarding,
  setOnboardingComplete,
  ShareLinksManager,
  demoShareLinksBloom,
  type OnboardingSlide,
} from "@jari-tools/shared";
import { BloomGarden } from "./components/BloomGarden";
import { BloomShareResult } from "./components/BloomShareResult";
import { PreviewNudge } from "./components/PreviewNudge";
import { QuestionStep } from "./components/QuestionStep";
import { QuestionsIntro } from "./components/QuestionsIntro";
import { ScenarioIntro } from "./components/ScenarioIntro";
import { ResultsIntro } from "./components/ResultsIntro";
import { ResultsSummary } from "./components/ResultsSummary";
import {
  BLOOM_DEBUG_QUESTION_INDEX,
  createDebugAnswers,
  isBloomDev,
  isScenarioPreview,
} from "./lib/debug";
import {
  getPreviewRunCount,
  incrementPreviewRunCount,
  shouldShowRepeatNudge,
} from "./lib/preview-runs";
import {
  BLOOM_LIKERT_COUNT,
  BLOOM_QUESTION_COUNT,
  shuffledBloomQuestions,
} from "./lib/questions";
import type { BloomQuestionWithTheme } from "./types/bloom-content";
import { computeThemeScores } from "./lib/scoring";
import type { BloomAnswers } from "./types/bloom-content";

const TOOL_SLUG = import.meta.env.VITE_TOOL_SLUG || "bloom";
const TOOL_NAME = "Bloom — ACT-profiel Jeugd";
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;

const ONBOARDING_SLIDES: OnboardingSlide[] = [
  {
    title: "Bloom",
    body: "Met Bloom ontdek je hoe je omgaat met gedachten, gevoelens, aandacht, jezelf, wat belangrijk voor je is en stappen zetten. Er zijn geen goede of foute antwoorden. Kies wat het beste bij jou past.",
  },
  {
    title: "Hoe het werkt",
    body: "Je krijgt één vraag per keer. Kies de antwoord die het beste bij jou past. Aan het eind zie je een overzicht per thema. Je kunt samen met je therapeut of begeleider bespreken wat dat voor jou betekent.",
  },
  {
    title: "Gratis en betaald",
    body: "Je kunt Bloom als behandelaar helemaal uitproberen: alle vragen en de tuin. Printen en delen met een cliënt zitten in de betaalde versie.",
  },
];

type BloomPhase = "intro" | "questions" | "scenario-intro" | "results-intro" | "results";
type Tab = "vragen" | "delen";

export function App() {
  const { access, name, loading, error, errorCode, isPaid, isGuest, toolId } = useAccess();
  const [tab, setTab] = useState<Tab>("vragen");
  const [previewRuns, setPreviewRuns] = useState(getPreviewRunCount);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !isScenarioPreview() && !hasCompletedOnboarding(TOOL_SLUG)
  );
  const [phase, setPhase] = useState<BloomPhase>(() =>
    isScenarioPreview() ? "questions" : "intro"
  );
  const [questionIndex, setQuestionIndex] = useState(() =>
    isScenarioPreview() ? BLOOM_LIKERT_COUNT : 0
  );
  const [answers, setAnswers] = useState<BloomAnswers>({});
  const [questionSequence, setQuestionSequence] = useState<BloomQuestionWithTheme[]>(
    () => shuffledBloomQuestions()
  );

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(TOOL_SLUG);
    setShowOnboarding(false);
  }, []);

  const resetQuestionnaire = useCallback(() => {
    setPhase("intro");
    setQuestionIndex(0);
    setAnswers({});
    setQuestionSequence(shuffledBloomQuestions());
  }, []);

  const startQuestions = useCallback(() => {
    if (!loading && !isPaid) {
      setPreviewRuns(incrementPreviewRunCount());
    }
    setPhase("questions");
  }, [loading, isPaid]);

  const skipToResults = useCallback(() => {
    setShowOnboarding(false);
    setAnswers(createDebugAnswers());
    setQuestionIndex(BLOOM_DEBUG_QUESTION_INDEX);
    setPhase("results");
  }, []);

  useEffect(() => {
    if (!isBloomDev()) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        skipToResults();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [skipToResults]);

  const showPreviewChrome = !loading && !isPaid;

  useEffect(() => {
    if (!showPreviewChrome) setNudgeOpen(false);
  }, [showPreviewChrome]);

  const currentQuestion = questionSequence[questionIndex];

  const startScenarios = useCallback(() => {
    setPhase("questions");
    setQuestionIndex(BLOOM_LIKERT_COUNT);
  }, []);

  const handleSelect = useCallback(
    (value: number) => {
      if (!currentQuestion) return;

      const isNewAnswer = answers[currentQuestion.id] === undefined;
      const nextAnswers = { ...answers, [currentQuestion.id]: value };
      setAnswers(nextAnswers);

      if (
        isNewAnswer &&
        showPreviewChrome &&
        shouldShowRepeatNudge(previewRuns, Object.keys(nextAnswers).length)
      ) {
        setNudgeOpen(true);
      }

      if (questionIndex < BLOOM_LIKERT_COUNT - 1) {
        setQuestionIndex((i) => i + 1);
      } else if (questionIndex === BLOOM_LIKERT_COUNT - 1) {
        setPhase("scenario-intro");
      } else if (questionIndex < BLOOM_QUESTION_COUNT - 1) {
        setQuestionIndex((i) => i + 1);
      } else {
        setPhase("results-intro");
      }
    },
    [answers, currentQuestion, questionIndex, showPreviewChrome, previewRuns]
  );

  const startResults = useCallback(() => {
    setPhase("results");
  }, []);

  const handleResultsIntroBack = useCallback(() => {
    setPhase("questions");
    setQuestionIndex(BLOOM_QUESTION_COUNT - 1);
  }, []);

  const handleBack = useCallback(() => {
    if (questionIndex === BLOOM_LIKERT_COUNT) {
      setPhase("scenario-intro");
      return;
    }
    if (questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
    }
  }, [questionIndex]);

  const handleScenarioIntroBack = useCallback(() => {
    setPhase("questions");
    setQuestionIndex(BLOOM_LIKERT_COUNT - 1);
  }, []);

  const themeScores =
    phase === "results" ? computeThemeScores(answers) : [];
  const flowerCount = Object.keys(answers).length;

  return (
    <>
      {tab === "vragen" ? (
        <BloomGarden flowerCount={flowerCount} dimmed={phase === "results"} />
      ) : null}
      <ToolLayout
        toolName={TOOL_NAME}
        access={access}
        userName={name}
        loading={loading}
        error={error}
        errorCode={errorCode}
        portalUrl={PORTAL_URL}
        isGuest={isGuest}
        toolId={toolId}
        toolSlug={TOOL_SLUG}
        onShowOnboarding={() => setShowOnboarding(true)}
        skipGuestPrompt
      >
        <ToolTabs
          ariaLabel="Bloom secties"
          activeId={tab}
          onSelect={(id) => setTab(id as Tab)}
          tabs={[
            { id: "vragen", label: "Vragen" },
            { id: "delen", label: "Delen" },
          ]}
        />

        <div
          id="jari-tool-panel-vragen"
          role="tabpanel"
          aria-labelledby="jari-tool-tab-vragen"
          hidden={tab !== "vragen"}
        >
          {tab === "vragen" ? (
          <div className="bloom-workspace">
            {showPreviewChrome ? (
              <p className="bloom-preview-banner">
                Preview voor behandelaars — niet bedoeld om door een cliënt te laten invullen.
              </p>
            ) : null}

            {phase === "intro" && <QuestionsIntro onStart={startQuestions} />}

            {phase === "scenario-intro" && (
              <ScenarioIntro onContinue={startScenarios} onBack={handleScenarioIntroBack} />
            )}

            {phase === "questions" && currentQuestion && (
              <QuestionStep
                question={currentQuestion}
                questionIndex={questionIndex}
                totalQuestions={BLOOM_QUESTION_COUNT}
                selectedValue={answers[currentQuestion.id]}
                onSelect={handleSelect}
                onBack={handleBack}
                canGoBack={questionIndex > 0}
              />
            )}

            {phase === "results-intro" && (
              <ResultsIntro onContinue={startResults} onBack={handleResultsIntroBack} />
            )}

            {phase === "results" && (
              <ResultsSummary
                scores={themeScores}
                onRestart={resetQuestionnaire}
                isPaid={!showPreviewChrome}
                portalUrl={PORTAL_URL}
              />
            )}
          </div>
          ) : null}
        </div>

        <div
          id="jari-tool-panel-delen"
          role="tabpanel"
          aria-labelledby="jari-tool-tab-delen"
          hidden={tab !== "delen"}
        >
          {tab === "delen" ? (
          <ShareLinksManager
            toolSlug={TOOL_SLUG}
            access={access}
            configFields={[]}
            introText="Maak een link om de vragenlijst met een cliënt te delen. In het overzicht zie je de voortgang en het resultaat."
            demoLinks={demoShareLinksBloom()}
            renderResult={(data) => <BloomShareResult data={data} />}
          />
          ) : null}
        </div>
      </ToolLayout>

      {nudgeOpen ? (
        <PreviewNudge
          onDismiss={() => setNudgeOpen(false)}
          portalUrl={PORTAL_URL}
          toolSlug={TOOL_SLUG}
        />
      ) : null}

      <Onboarding
        open={showOnboarding}
        slides={ONBOARDING_SLIDES}
        onComplete={completeOnboarding}
      />
    </>
  );
}
